import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Renderer2, } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { GuidedTour, GuidedTourService, Orientation } from 'ngx-guided-tour';
import { MenuItem } from 'primeng/api';
import { AuthConfig, AuthService, AuthUser } from 'src/app/Services/auth.service';
import { MessagingService } from 'src/app/Services/messaging.service';
import { OnBoardingService } from 'src/app/Services/shared/onboarding.service';
import { ShareService } from 'src/app/Services/shared/share.service';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.css'],
    standalone: false
})
export class SidebarComponent implements OnInit, AfterViewInit {
    @Input() hideLogo: boolean = false;
    public isAuthenticated: boolean = false;
    public authConfig!: AuthConfig;
    public items!: MenuItem[];
    public artistId!: string;
    public authUser: AuthUser | null = null;
    public isArtist: boolean = false;
    public isGallery: boolean = false;
    public isFreePlanUser: boolean = true;
    public showSidebar: boolean = true;
    public onCloseSidebar = new EventEmitter<boolean>();
    public currentStepIndex = 0;
    public artistTour: GuidedTour = {
        tourId: 'artist-tour',
        useOrb: false,
        preventBackdropFromAdvancing: true,
        steps: [
            {
                title: 'Dashboard',
                content: this.getSanitizedContent(`
                    <div class="row">
                        <div class="col-12">
                            <div class="dsTourDescription">
                                <p>The <strong>ArtFlow Pro Dashboard</strong> serves as the central hub for artists, offering a comprehensive overview of their professional activity. </p>
                                <div class="dsButton" id="toggleButton">
                                    <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                    <span class="toggleText" id="toggleText">More</span>
                                </div>
                                <div class="dsMore" id="moreInfo" style="display:none;">
                                <p>Here’s what you’ll find:</p>
                                <ul style="margin-left:40px">
                                    <li>
                                        <p><strong>Key Performance Data:</strong> Instantly see crucial metrics like total sales, top-selling artworks, and engagement stats to gauge your success at a glance.</p>
                                    </li>
                                    <li>
                                        <p><strong>Contacts with Galleries:</strong> Keep track of all your gallery relationships in one place, with quick access to contact details, partnership status, and recent interactions.</p>
                                    </li>
                                    <li>
                                        <p><strong>Performance Graphs:</strong> Visualize your progress with dynamic graphs that track sales trends, artwork views, and other key indicators over time.</p>
                                    </li>
                                    <li>
                                        <p><strong>Recent Sales:</strong> Stay informed with a snapshot of your latest sales, including details like the buyer, gallery, and sale price.</p>
                                    </li>
                                    <li>
                                        <p><strong>New Artworks Uploaded:</strong> View a list of your most recent uploads, complete with thumbnail previews and quick links to edit or share your work.</p>
                                    </li>
                                </ul>
                                <p>This dashboard ensures you have all the information you need to manage your career efficiently, all in one place.</p>
                            </div>
                        </div>
                    </div>`),
                selector: '.dashboard',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Your artworks',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>The "Your Artworks" screen in the ArtFlow Pro artist portal app is designed to provide a comprehensive and organized view of all your artworks, both current and past. </p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" id="moreInfo" style="display: none">
                                <p>Here’s what you’ll find:</p>
                                <p><strong>Library of Artworks</strong>: Easily browse through your complete collection of artworks, including both current pieces and those you’ve created in the past.</p>
                                <p><strong>Item Cards</strong>: Each artwork is represented by an item card that includes:</p>
                                <ul style="margin-left:40px">
                                    <li>A thumbnail image for quick visual identification.</li>
                                    <li>Key details such as title, creation date, and medium.</li>
                                    <li style="list-style:none"><img src="https://artflow-pro.com/images/item-card.jpg" alt="Item Card Thumbnail"></li>
                                </ul>
                                <p><strong>Status Badges</strong>: Every item card features a badge that clearly indicates the current status of the artwork, such as "Available," "Sold," "In Gallery," or "Pending Review."</p>
                                <p><strong>Clickable Details</strong>: Simply click on any item card to access full details about the artwork, including high-resolution images, description, gallery locations, sales history, and more.</p>
                                <p><strong>Advanced Filtering</strong>: Refine your view using advanced filtering options to sort artworks by:</p>
                                <ul style="margin-left:40px">
                                    <li><strong>Art Type</strong> (e.g., painting, sculpture, digital)</li>
                                    <li><strong>Category</strong> (e.g., abstract, landscape, portrait)</li>
                                    <li><strong>Status</strong> (e.g., available, sold, in gallery)</li>
                                    <li><strong>Gallery</strong> (e.g., gallery name, location)</li>
                                </ul>
                                <p>This screen is your central hub for managing and reviewing your artistic portfolio, offering both quick insights and deep dives into your creative work.</p>
                        </div>
                    </div>
                </div>`),
                selector: '.yourartworks',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Proposals',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>The Proposals screen in the ArtFlow Pro artist portal app is designed to streamline the process of presenting your artworks to galleries for potential representation and sale. </p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display: none">
                                <p>Key features include:</p>
                                <ul style="margin-left:40px">
                                    <li><strong>Propose Artworks:</strong> Easily select and propose artworks that a gallery might be interested in from your inventory.</li>
                                    <li><strong>Drag-and-Drop Functionality:</strong> Conveniently drag and drop artworks from your current inventory list directly into a proposal document.</li>
                                    <li><strong>Send Your Proposal:</strong> Send your proposal to one of your "Connected Galleries" with just a few clicks.</li>
                                    <li><strong>Gallery Responses:</strong> Receive responses from the gallery, where they can accept, decline, or select specific artworks they are interested in.</li>
                                    <li><strong>Create Dispatch Documentation:</strong> For artworks selected by the gallery, generate the necessary dispatch documentation to assign those pieces for gallery representation and sale.</li>
                                </ul>
                                <p>This screen simplifies the entire proposal process, allowing artists to efficiently manage gallery interactions and focus on their creative work.</p>
                        </div>
                    </div>
                </div>`),
                selector: '.proposal',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Consignments',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>The Consignment screen in the ArtFlow Pro artist portal app helps you manage and document the consignment of your artworks to galleries. </p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display: none">
                                <p>This screen provides the following features:</p>
                                <ul style="margin-left:40px">
                                    <li>Create accurate dispatch documentation for each artwork, detailing all necessary information.</li>
                                    <li>Include the following information for each artwork:
                                        <ul style="margin-left:40px">
                                            <li>Title</li>
                                            <li>Serial Number</li>
                                            <li>Marketing Descriptions</li>
                                            <li>Image</li>
                                            <li>Pricing</li>
                                            <li>Marketing Features</li>
                                            <li>Size Information</li>
                                            <li>Details on Materials Used</li>
                                        </ul>
                                    </li>
                                    <li>Include your <strong>Payment Details</strong> for easy processing.</li>
                                    <li>Include a <strong>Terms and Conditions</strong> statement to outline the agreement.</li>
                                    <li>Indicate how you will <strong>Deliver Your Artworks</strong> to the gallery, whether by shipping, personal delivery, or another method.</li>
                                    <li>The gallery will <strong>Approve the Dispatch</strong> after reviewing the documentation.</li>
                                    <li>Upon delivery, the gallery will provide you with a <strong>Delivery Report</strong> on the condition of the artworks.</li>
                                </ul>
                                <p>This screen simplifies the entire delivery and dispatch process, allowing artists to efficiently manage gallery interactions and focus on their creative work.</p>
                            </div>
                        </div>
                    </div>`),
                selector: '.consignments',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Sync Products',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Sync Products</h3>
                        <div class="dsTourDescription">
                            <p>Sync your artworks with popular e-commerce platforms:</p>
                            <div class="dsButton" id="toggleButtonSync">
                                <i class="fa fa-arrow-down" id="arrowIconSync" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleTextSync">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <ul style="margin-left:40px">
                                    <li>Connect to Shopify, WooCommerce, Wix, Square POS, or Squarespace.</li>
                                    <li>Sync product details and inventory in real-time.</li>
                                    <li>Manage sales and updates across multiple platforms.</li>
                                    <li>Expand your reach with seamless online store integration.</li>
                                </ul>
                                <p>Focus on your art while your products sync effortlessly online.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.productsync',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Labels Generation',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Labels Generation</h3>
                        <div class="dsTourDescription">
                            <p>Create professional info cards and barcode stickers for your artworks:</p>
                            <div class="dsButton" id="toggleButtonLabels">
                                <i class="fa fa-arrow-down" id="arrowIconLabels" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleTextLabels">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <ul style="margin-left:40px">
                                    <li>Select artworks to generate info cards or barcode stickers.</li>
                                    <li>Preview product details before creating documents.</li>
                                    <li>Download PDFs for professional use in galleries or exhibitions.</li>
                                    <li>Streamline artwork labeling with customizable options.</li>
                                </ul>
                                <p>Effortlessly produce professional labels to showcase your art.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.labelsgeneration',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Connected Galleries',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <div class="connected-galleries-screen">
                                <p>This screen stores all the information about the galleries that currently represent your work.</p>
                                <div class="dsButton" id="toggleButton">
                                    <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                    <span class="toggleText" id="toggleText">More</span>
                                </div>
                                <div class="dsMore" style="display: none">
                                    <strong>Gallery Details:</strong>
                                    <ul style="margin-left:40px">
                                        <li><strong>Contact Details:</strong> Address, Phone, Email</li>
                                        <li><strong>Artworks on Hand:</strong> List of artworks with title, serial number, and image</li>
                                        <li><strong>Artworks Sold:</strong> List of sold artworks with title, serial number, and image</li>
                                        <li><strong>Real-time Chat:</strong> <a href="#">Open Messenger</a></li>
                                        <li><strong>Confidential Notes:</strong> Confidential notes about the gallery</li>
                                    </ul>
                                    <p><strong>Invite Gallery to Review Your Artwork</strong></p>
                                    <p>From this page, you can invite a gallery to review your artwork with a view to representing you. You can also invite one of your existing galleries to join you on ArtFlow Pro.</p>
                                    <p><strong>Artwork Details</strong></p>
                                    <p>Each artwork listed under a gallery includes:</p>
                                    <ul style="margin-left:40px">
                                        <li>Title</li>
                                        <li>Serial Number</li>
                                        <li>Marketing Descriptions</li>
                                        <li>Image</li>
                                        <li>Pricing</li>
                                        <li>Marketing Features</li>
                                        <li>Size Information</li>
                                        <li>Details on the Material Used</li>
                                    </ul>
                                    <p><strong>Payment &amp; Delivery Information</strong></p>
                                    <ul style="margin-left:40px">
                                        <li><strong>Payment Details:</strong> Your payment information</li>
                                        <li><strong>Terms and Conditions:</strong> Your terms and conditions statement</li>
                                        <li><strong>Delivery Method:</strong> Indicate how you will deliver your artworks to the gallery</li>
                                    </ul>
                                    <p><strong>Gallery Approval &amp; Delivery Report</strong></p>
                                    <p>The gallery will approve the dispatch and provide you with a delivery report on the condition of the artworks.</p>
                                </div>
                            </div>
                        </div>
                    </div>`),
                selector: '.connectedgalleries',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Product Request',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>The Product Request screen in the ArtFlow Pro artist portal app provides you with a seamless way to showcase your available artworks to selected galleries and manage representation requests.</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display: none">
                                <p><strong>Gallery Requests:</strong></p>
                                <p>You can allow selected galleries to view your currently available artworks and request them.</p>
                                <ul style="margin-left:40px">
                                    <li>Galleries can view the artworks you've made available and send requests to represent them.</li>
                                    <li>You'll receive these requests directly within the app, providing you with a convenient way to manage gallery interest.</li>
                                </ul>
                                <p><strong>Request Review & Decision:</strong></p>
                                <ul style="margin-left:40px">
                                    <li>Review each request to determine whether you wish to move forward with the gallery's offer.</li>
                                    <li>Decide if the gallery's request aligns with your representation goals before accepting or declining.</li>
                                </ul>
                                <p><strong>Automated Documentation:</strong></p>
                                <ul style="margin-left:40px">
                                    <li>Upon accepting a request, the system will automatically generate the necessary delivery documentation.</li>
                                    <li>This includes all details required for sending the artwork to the gallery, making the process seamless and efficient.</li>
                                </ul>
                                <p><strong>Quick Response to Gallery Interest:</strong> The Product Request screen allows galleries to quickly request representation of your new works, ensuring timely and efficient collaboration between artists and galleries.</p>
                            </div>
                        </div>
                    </div>`),
                selector: '.productrequest',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Your Messages',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>The "Your Messages" screen in the ArtFlow Pro artist portal app provides a powerful communication hub designed to enhance your collaboration with galleries.</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display: none">
                                <p><strong>Here's what ArtFlow Pro's Your Messages feature offers you:</strong></p>
                                <ul style="margin-left:40px">
                                    <li>
                                        <p><strong>Real-Time Chat</strong>: Instantly connect with gallery staff through real-time messaging. Whether you need to discuss a new proposal, update an ongoing project, or address urgent issues, quick and direct communication ensures smooth and timely interactions.</p>
                                    </li>
                                    <li>
                                        <p><strong>Conversation Records</strong>: Automatically keep a complete record of all conversations. Every message, whether it’s a casual check-in or a detailed discussion, is securely stored and easily accessible for future reference. This helps in maintaining clarity and ensuring nothing gets overlooked.</p>
                                    </li>
                                    <li>
                                        <p><strong>Delivery Coordination</strong>: Use the messaging system to coordinate deliveries with galleries. Discuss and confirm delivery details, timing, and any special requirements directly within the app. This minimizes the risk of misunderstandings and helps in streamlining the delivery process.</p>
                                    </li>
                                    <li>
                                        <p><strong>Open Communication Channels</strong>: Keep communication channels open to continuously improve marketing performance. Regular updates, feedback exchanges, and collaborative planning through the app can lead to more effective marketing strategies and stronger gallery relationships.</p>
                                    </li>
                                </ul>
                                <p><strong>Benefits of Using the "Your Messages" System:</strong></p>
                                <ul style="margin-left:40px">
                                    <li><strong>Enhanced Collaboration</strong>: Real-time chat capabilities ensure you and your galleries are always on the same page, leading to smoother collaborations.</li>
                                    <li><strong>Improved Organization</strong>: The automatic logging of conversations provides an organized, searchable history, reducing the likelihood of miscommunication and missed details.</li>
                                    <li><strong>Streamlined Operations</strong>: Coordinating deliveries directly through the messaging system helps to ensure that artwork reaches its destination efficiently and on time.</li>
                                    <li><strong>Better Marketing Outcomes</strong>: Continuous, open communication fosters better relationships and allows for more responsive and agile marketing efforts, ultimately leading to greater success in promoting and selling your art.</li>
                                </ul>
                            </div>
                        </div>
                    </div>`),
                selector: '.yourmessages',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Portfolio & Social',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>The "Portfolio & Social" section is designed to help you manage and showcase your portfolio while integrating social media accounts for seamless sharing.</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display: none">
                                <p>This section includes:</p>
                                <ul style="margin-left:40px">
                                    <li><strong>Portfolio</strong>: View and manage your complete collection of artworks. Each item is displayed with essential details, thumbnails, and status badges for better organization.</li>
                                    <li><strong>Social Media Shares</strong>: Easily share your artworks across connected social media platforms to expand your reach and engagement.</li>
                                    <li><strong>Social Accounts</strong>: Link and manage your social media accounts for hassle-free sharing directly from the platform.</li>
                                </ul>
                                <p>Explore this section to streamline your portfolio management and enhance your online presence effectively.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.portfolioandsocial',
                orientation: Orientation.Right,
            },
        ],
        completeCallback: () => this.onArtistTourComplete(),
        skipCallback: () => this.onArtistTourSkip(),
    };
    public galleryTour: GuidedTour = {
        tourId: 'gallery-tour',
        useOrb: false,
        preventBackdropFromAdvancing: true,
        steps: [
            {
                title: 'Dashboard',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Dashboard</h3>
                        <div class="dsTourDescription">
                            <p>The <strong>ArtFlow Pro Dashboard</strong> is your central hub, providing an overview of your professional activity. It includes:</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                               <ul style="margin-left:40px">
                                <li><strong>Key Performance Data:</strong> View metrics like total sales, top-selling artworks, and engagement stats at a glance.</li>
                                <li><strong>Contacts with Galleries:</strong> Track gallery relationships with access to contact details, partnership status, and recent interactions.</li>
                                <li><strong>Performance Graphs:</strong> Visualize sales trends and key indicators with dynamic graphs.</li>
                                <li><strong>Recent Sales:</strong> Get a snapshot of recent sales including buyer details, gallery information, and sale price.</li>
                                <li><strong>New Artworks Uploaded:</strong> View recent uploads with thumbnails and links for editing or sharing.</li>
                               </ul>
                               <p>Manage your career efficiently with all necessary information consolidated in one place.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.dashboard',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Artwork Inventory',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Artwork Inventory</h3>
                        <div class="dsTourDescription">
                            <p>Manage and showcase your artworks to galleries with these features:</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <ul style="margin-left:40px">
                                    <li><strong>Gallery Requests:</strong> Allow galleries to view and request your artworks.</li>
                                    <li><strong>Request Review & Decision:</strong> Review and decide on gallery requests.</li>
                                    <li><strong>Automated Documentation:</strong> Generate delivery documentation upon request acceptance.</li>
                                    <li><strong>Quick Response:</strong> Efficiently handle gallery requests for timely collaboration.</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.artworkinventry',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Proposals',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Proposals</h3>
                        <div class="dsTourDescription">
                            <p>The Proposals screen streamlines presenting your artworks to galleries:</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                               <ul style="margin-left:40px">
                                <li><strong>Propose Artworks:</strong> Select artworks to propose to galleries.</li>
                                <li><strong>Drag-and-Drop Functionality:</strong> Drag artworks into proposals.</li>
                                <li><strong>Send Your Proposal:</strong> Send proposals to connected galleries easily.</li>
                                <li><strong>Gallery Responses:</strong> Receive and manage gallery responses.</li>
                                <li><strong>Create Dispatch Documentation:</strong> Generate documentation for artworks selected by galleries.</li>
                               </ul>
                               <p>Simplify the proposal process and focus on your creative work.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.proposal',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Consignments',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Consignments</h3>
                        <div class="dsTourDescription">
                            <p>Manage and document artwork consignments with these features:</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <ul style="margin-left:40px">
                                    <li>Create dispatch documentation with detailed artwork information.</li>
                                    <li>Include payment details and terms and conditions.</li>
                                    <li>Specify delivery method and gallery approval process.</li>
                                    <li>Receive a delivery report from the gallery.</li>
                                </ul>
                                <p>Efficiently manage consignments and focus on your art.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.consignments',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Labels Generation',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Labels Generation</h3>
                        <div class="dsTourDescription">
                            <p>Create professional info cards and barcode stickers for your artworks:</p>
                            <div class="dsButton" id="toggleButtonLabels">
                                <i class="fa fa-arrow-down" id="arrowIconLabels" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleTextLabels">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <ul style="margin-left:40px">
                                    <li>Select artworks to generate info cards or barcode stickers.</li>
                                    <li>Preview product details before creating documents.</li>
                                    <li>Download PDFs for professional use in galleries or exhibitions.</li>
                                    <li>Streamline artwork labeling with customizable options.</li>
                                </ul>
                                <p>Effortlessly produce professional labels to showcase your art.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.labelsgeneration',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Connected Artist',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Connected Artist</h3>
                        <div class="dsTourDescription">
                            <p>Manage relationships with artists connected to your gallery:</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <ul style="margin-left:40px">
                                    <li><strong>Key Performance Data:</strong> View metrics like total sales, top-selling artworks, and engagement stats at a glance.</li>
                                    <li><strong>Contacts with Artists:</strong> Track artist relationships with access to contact details, partnership status, and recent interactions.</li>
                                    <li><strong>Performance Graphs:</strong> Visualize sales trends and key indicators with dynamic graphs.</li>
                                    <li><strong>Recent Sales:</strong> Get a snapshot of recent sales including buyer details, artist information, and sale price.</li>
                                    <li><strong>New Artworks Uploaded:</strong> View recent uploads with thumbnails and links for editing or sharing.</li>
                                </ul>
                                <p>Manage your artist relationships efficiently with all necessary information consolidated in one place.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.connectedartist',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Request Product',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>Galleries can request specific products from artists for exhibitions or sales directly through the app.</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                               <p><strong>Request Process:</strong> Galleries browse available products, submit requests, and track responses from artists.</p>
                               <p><strong>Approval:</strong> Artists review and accept or decline requests based on product availability.</p>
                               <p><strong>Delivery:</strong> Once approved, delivery details are automatically generated for seamless processing.</p>
                               <p>This feature enables quick and efficient collaboration between galleries and artists.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.requestedproducts',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Gallery Products',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Gallery Products</h3>
                        <div class="dsTourDescription">
                            <p>Access a comprehensive view of all your gallery's products on the "Your Products" screen:</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <p><strong>Product Library:</strong> Browse through your collection, including current and past items.</p>
                                <p><strong>Product Cards:</strong> Each product is displayed with:</p>
                                <ul style="margin-left:40px">
                                    <li>A thumbnail image for quick identification.</li>
                                    <li>Details such as product name, creation date, and medium.</li>
                                </ul>
                                <p><strong>Status Badges:</strong> See the status of each product, such as "Available," "Sold," or "In Stock."</p>
                                <p><strong>Clickable Details:</strong> Click on product cards for full details, including high-resolution images and sales history.</p>
                                <p><strong>Advanced Filtering:</strong> Sort products by type, category, status, or artist.</p>
                                <p>This screen is your hub for managing and reviewing your gallery's portfolio.</p> 
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.view-gallery-products',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Your Messages',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <h3>Your Messages</h3>
                        <div class="dsTourDescription">
                            <p>Enhance communication with galleries through the "Your Messages" feature:</p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <ul style="margin-left:40px">
                                    <li><strong>Real-Time Chat:</strong> Connect with gallery staff instantly for discussions and updates.</li>
                                    <li><strong>Conversation Records:</strong> Automatically store and access all message records.</li>
                                    <li><strong>Delivery Coordination:</strong> Discuss and confirm delivery details directly through the app.</li>
                                    <li><strong>Open Communication Channels:</strong> Foster continuous improvement and better marketing outcomes through regular updates and feedback.</li>
                                </ul>
                                <p>Improve collaboration, organization, and efficiency with effective messaging tools.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.yourmessages',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
            {
                title: 'Portfolio & Social',
                content: this.getSanitizedContent(`
                <div class="row">
                    <div class="col-12">
                        <div class="dsTourDescription">
                            <p>The "Portfolio & Social" section is designed to help you manage and showcase your portfolio while integrating social media accounts for seamless sharing. </p>
                            <div class="dsButton" id="toggleButton">
                                <i class="fa fa-arrow-down" id="arrowIcon" aria-hidden="true"></i> 
                                <span class="toggleText" id="toggleText">More</span>
                            </div>
                            <div class="dsMore" style="display:none">
                                <p>This section includes:</p>
                                <ul>
                                    <li><strong>Portfolio</strong>: View and manage your complete collection of artworks. Each item is displayed with essential details, thumbnails, and status badges for better organization.</li>
                                    <li><strong>Social Media Shares</strong>: Easily share your artworks across connected social media platforms to expand your reach and engagement.</li>
                                    <li><strong>Social Accounts</strong>: Link and manage your social media accounts for hassle-free sharing directly from the platform.</li>
                                </ul>
                                <p>Explore this section to streamline your portfolio management and enhance your online presence effectively.</p>
                            </div>
                        </div>
                    </div>
                </div>`),
                selector: '.portfolioandsocial',
                orientation: Orientation.Right,
                closeAction: () => this.goToNextStep(),
            },
        ],
        completeCallback: () => this.onGalleryTourComplete(),
        skipCallback: () => this.onGalleryTourSkip(),
    };
    public showDashboard: boolean = false;
    public isOnDashboard: boolean = false;
    public showQuickTour: boolean = false;
    public showMore: boolean = false;
    public isPortfolioSocialOpen: boolean = false;
    public isHovering: boolean = false;
    public countUnreadchatbox!: number;

    constructor(
        private authService: AuthService,
        private renderer: Renderer2,
        private el: ElementRef,
        private shareService: ShareService,
        private messagingService: MessagingService,
        private sanitizer: DomSanitizer,
        private route: ActivatedRoute,
        private router: Router,
        private guidedTourService: GuidedTourService,
        private onBoardingService: OnBoardingService
    ) { }

    public receiveMessages() {
        this.messagingService.currentMessage.subscribe((data: any) => {
            this.CountUnreadchatbox();
        });
    }

    public CountUnreadchatbox(): void {
        this.messagingService.CountUnreadchatbox().subscribe(
            (data) => {
                this.countUnreadchatbox = data;
                this.initializeMenuItems();
            },
            (error) => {
                console.error('Error fetching notifications:', error);
            }
        );
    }

    public ngOnInit(): void {
        this.shareService.visibility$.subscribe((visibility) => {
            this.showSidebar = visibility.sidebar;
        });
        this.isAuthenticated = this.authService.isAuthenticated();

        if (this.isAuthenticated) {
            this.authConfig = this.authService.getAuthConfig();
            this.authUser = this.authService.getCurrentUser();
            this.isArtist = this.authConfig.IsArtist || false;
            this.isGallery = this.authConfig.IsGallery || false;
            this.isFreePlanUser = this.authService.isFreePlanUser();
            if (this.isArtist || this.isGallery) {
                this.receiveMessages();
            }
            if (this.authConfig.IsSuperAdmin || this.authConfig.IsAdministrator) {
                this.route.params.subscribe((params) => {
                    if (params['artistId']) {
                        this.artistId = params['artistId'];
                    }
                });
            }
            this.initializeMenuItems();

        }
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.isOnDashboard = this.router.url === '/dashboard';
                this.updateQuickTourVisibility();

            }
        });
    }

    private updateQuickTourVisibility() {
        this.showQuickTour = this.isOnDashboard && (this.isArtist || this.isGallery);
        this.initializeMenuItems();
    }

    getSanitizedContent(rawContent: string): string {
        const safeHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(rawContent);
        return safeHtml as unknown as string;
    }

    toggleMore() {
        this.showMore = !this.showMore;
        this.updateTourContent();
    }

    updateTourContent() {
        const buttonText = document.querySelector('span.toggleText') as HTMLElement;
        const arrowIcon = document.querySelector('i.fa') as HTMLElement;
        const moreInfo = document.querySelector('div.dsMore') as HTMLElement;
        if (buttonText && arrowIcon && moreInfo) {
            if (this.showMore) {
                buttonText.textContent = 'Hide';
                arrowIcon.classList.remove('fa-arrow-down');
                arrowIcon.classList.add('fa-arrow-up');
                moreInfo.style.display = 'block';
            } else {
                buttonText.textContent = 'More';
                arrowIcon.classList.remove('fa-arrow-up');
                arrowIcon.classList.add('fa-arrow-down');
                moreInfo.style.display = 'none';
            }
        } else {
            console.error('One or more elements not found in the DOM.');
        }
    }

    onGalleryTourComplete() {
        console.log('Tour completed');
        this.onBoardingService.updateLocalStorageState(false);
        this.showDashboard = false;
    }

    onGalleryTourSkip() {
        console.log('Tour skipped');
        this.onBoardingService.updateLocalStorageState(false);
        this.showDashboard = false;
    }

    onArtistTourComplete() {
        console.log('Tour completed');
        this.onBoardingService.updateLocalStorageState(false);
        this.showDashboard = false;
    }

    onArtistTourSkip() {
        console.log('Tour skipped');
        this.onBoardingService.updateLocalStorageState(false);
        this.showDashboard = false;
    }

    private initializeMenuItems() {
        // Check if user is event-only artist (restricted access)
        const isEventArtist = this.authService.isEventArtist();

        var authConfig = {
            isSuperAdmin: this.authService.IsSuperAdmin(),
            isAdministrator: this.authService.isAdministrator(),
            isArtist: this.authService.isArtist(),
            isGallery: this.authService.isGallery(),
            isGalleryStaff: this.authService.isGalleryStaff(),
            isEventArtist: isEventArtist, // Add event-only artist flag
            isJudge: this.authService.isJudge(),     // Judge role check
            isCurator: this.authService.isCurator(), // Curator role check
        };

        this.isOnDashboard = this.router.url === '/dashboard';

        // ============================================================================
        // JUDGE MINIMAL MENU
        // Show only: Dashboard
        // ============================================================================
        if (authConfig.isJudge) {
            this.items = [
                {
                    label: 'Dashboard',
                    icon: 'fa fa-dashboard',
                    routerLink: ['/judge/events'],
                    styleClass: 'dashboard',
                },
            ];
            return; // Exit early for judge
        }

        // ============================================================================
        // CURATOR MINIMAL MENU
        // Show only: Dashboard
        // ============================================================================
        if (authConfig.isCurator) {
            this.items = [
                {
                    label: 'Dashboard',
                    icon: 'fa fa-dashboard',
                    routerLink: ['/curator/events'],
                    styleClass: 'dashboard',
                },
            ];
            return; // Exit early for curator
        }

        // ============================================================================
        // EVENT-ONLY ARTIST RESTRICTED MENU
        // Show EXACTLY these 6 items for event-only artists:
        // 1. My Exhibitions / Participated Events
        // 2. Submit Artworks
        // 3. Connected Galleries
        // 4. Messages
        // 5. My Profile
        // 6. Logout (automatically handled by app.component)
        // ============================================================================


        // ============================================================================
        // FULL MENU (for non-event-only artists, galleries, admins)
        // ============================================================================
        this.items = [
            {
                label: 'Favorites',
                icon: 'fa fa-heart',
                items: [
                    {
                        label: 'Dashboard',
                        icon: 'fa fa-dashboard',
                        routerLink: ['/ds-dashboard'],
                        styleClass: 'dashboard',
                        visible: authConfig.isArtist && !authConfig.isEventArtist,
                    },
                    {
                        label: 'Dashboard',
                        icon: 'fa fa-dashboard',
                        routerLink: ['/ds-gallery-dashboard'],
                        styleClass: 'dashboard',
                        visible: authConfig.isGallery || authConfig.isGalleryStaff,
                    },
                    {
                        label: 'Dashboard',
                        icon: 'fa fa-dashboard',
                        routerLink: ['/ds-admin-dashboard'],
                        styleClass: 'dashboard',
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },
                    {
                        label: 'Admins',
                        icon: 'fa fa-users',
                        routerLink: ['/admins'],
                        styleClass: 'admin',
                        visible: authConfig.isSuperAdmin,
                    },
                    {
                        label: 'Artworks Inventory',
                        icon: 'fa fa-cubes',
                        routerLink: ['/galleries', 'gallery-products'],
                        styleClass: 'artworkinventry',
                        visible: authConfig.isGallery || authConfig.isGalleryStaff,
                    },
                    {
                        label: 'Artists',
                        icon: 'fa fa-users',
                        routerLink: ['/admin', 'view'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },

                    {
                        label: 'Your Events',
                        icon: 'fa fa-trophy',
                        routerLink: ['/events'],
                        styleClass: 'competitions',
                        visible: authConfig.isGallery 
                    },
                    {
                        label: 'Your Artworks',
                        icon: 'fa fa-briefcase',
                        routerLink: ['/artists', 'artworks'],
                        styleClass: 'yourartworks',
                        visible: authConfig.isArtist, // Show for ALL artists (both full and event-only)
                    },

                    {
                        label: 'Your Events',
                        icon: 'fa fa-trophy',
                        routerLink: ['/events', 'my-participated-events'],
                        styleClass: 'my-events',
                        visible: authConfig.isArtist // Show for ALL artists (both full and event-only)
                    },

                    // {
                    //     label: 'Upcoming Events',
                    //     icon: 'fa fa-calendar',
                    //     routerLink: ['/events', 'available'],
                    //     styleClass: 'available-events',
                    //     visible: authConfig.isArtist // Show for ALL artists (both full and event-only)
                    // },

                 

                    {
                        label: 'Proposals',
                        icon: 'fa fa-file',
                        routerLink: ['/view-proposals'],
                        styleClass: 'proposal',
                        visible: (authConfig.isArtist && !authConfig.isEventArtist) || authConfig.isGallery, // Hide for event-only artists
                    },
                    {
                        label: 'Consignments',
                        icon: 'fa fa-truck',
                        routerLink: ['/view-consignments'],
                        styleClass: 'consignments',
                        visible: !authConfig.isEventArtist, // Hide for event-only artists
                    },
                    {
                        label: 'Sync Products',
                        icon: 'fa fa-refresh',
                        routerLink: ['sync-products'],
                        styleClass: 'productsync',
                        visible: authConfig.isArtist && !authConfig.isEventArtist, // Hide for event-only artists
                    },
                    {
                        label: 'Labels Generation',
                        icon: 'fa fa-barcode',
                        routerLink: ['/labels-generation'],
                        styleClass: 'labelsgeneration',
                        visible: authConfig.isGallery || (authConfig.isArtist && !authConfig.isEventArtist), // Hide for event-only artists
                    },
                    {
                        label: 'Connected Artists',
                        icon: 'fa fa-handshake-o',
                        routerLink: ['/galleries', 'connected-artists'],
                        styleClass: 'connectedartist',
                        visible: authConfig.isGallery || authConfig.isGalleryStaff,
                    },
                    {
                        label: 'Galleries',
                        icon: 'fa fa-file-image-o',
                        routerLink: ['/admin', 'view-galleries'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },
                    {
                        label: 'Connected Galleries',
                        icon: 'fa fa-handshake-o',
                        routerLink: ['/artists', 'connected-galleries'],
                        styleClass: 'connectedgalleries',
                        visible: authConfig.isArtist && !authConfig.isEventArtist, // Hide for event-only artists
                    },
                    {
                        label: 'Requested Products',
                        icon: 'fa fa-paper-plane',
                        routerLink: ['/galleries', 'requested-products'],
                        styleClass: 'requestedproducts',
                        visible: authConfig.isGallery || authConfig.isGalleryStaff,
                    },
                    {
                        label: 'Gallery Products',
                        icon: 'fa fa-briefcase',
                        routerLink: ['/galleries', 'view-gallery-products'],
                        styleClass: 'view-gallery-products',
                        visible: authConfig.isGallery,
                    },
                    {
                        label: 'Product Requests',
                        icon: 'fa fa-paper-plane',
                        routerLink: ['/artists', 'product-requests'],
                        styleClass: 'productrequest',
                        visible: authConfig.isArtist && !authConfig.isEventArtist, // Hide for event-only artists
                    },
                    {
                        label: `Your Messages ${this.countUnreadchatbox > 0 ? `(${this.countUnreadchatbox})` : ''}`,
                        icon: 'fa fa-comment',
                        routerLink: ['/inbox'],
                        styleClass: 'yourmessages',
                        visible: ((authConfig.isArtist && !authConfig.isEventArtist) || authConfig.isGallery || authConfig.isGalleryStaff) && this.authService.IsChatboxMessages(), // Hide for event-only artists
                    },
                    {
                        label: 'Rulesets',
                        icon: 'fa fa-credit-card',
                        routerLink: ['/subscription-rules'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },
                    {
                        label: 'System Settings',
                        icon: 'fa fa-gear',
                        routerLink: ['/system-settings'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },
                    {
                        label: 'Application Logs',
                        icon: 'fa fa-exclamation-triangle',
                        routerLink: ['/admin', 'application-log'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },
                ],
            },
            {
                label: 'Portfolio & Social',
                icon: 'fa fa-cogs',
                visible: authConfig.isGallery || (authConfig.isArtist && !authConfig.isEventArtist), // Hide entire section for event-only artists
                items: [
                    {
                        label: 'Portfolio',
                        icon: 'fa fa-folder',
                        routerLink: ['galleries', 'portfolio'],
                        visible: this.authConfig.IsGallery,
                    },
                    {
                        label: 'Portfolio',
                        icon: 'fa fa-folder',
                        routerLink: ['/artists', 'portfolio'],
                        visible: this.authConfig.IsArtist && !authConfig.isEventArtist,
                    },
                    {
                        label: 'Social Media Shares',
                        icon: 'fa fa-share-alt',
                        routerLink: ['/social-media'],
                        visible: ((this.authConfig.IsArtist && !authConfig.isEventArtist) || this.authConfig.IsGallery) && this.authService.IsSocialMediaShares(),
                    },
                    {
                        label: 'Facebook Accounts',
                        icon: 'fa fa-facebook-square',
                        routerLink: ['/facebook-accounts'],
                        visible: ((this.authConfig.IsArtist && !authConfig.isEventArtist) || this.authConfig.IsGallery) && this.authService.IsFacebookAccounts(),
                    },
                    {
                        label: 'Instagram Accounts',
                        icon: 'fa fa-instagram',
                        routerLink: ['/instagram-account'],
                        visible: ((this.authConfig.IsArtist && !authConfig.isEventArtist) || this.authConfig.IsGallery) && this.authService.IsInstagramAccounts(),
                    },
                ],
            },
            {
                label: 'EMAIL & SMS HUB',
                icon: 'fa fa-envelope',
                visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                items: [
                    {
                        label: 'Email & Sms Events',
                        icon: 'fa fa-cog',
                        routerLink: ['/admin', 'email-events'],
                    },
                    {
                        label: 'Email Templates',
                        icon: 'fa fa-envelope',
                        routerLink: ['/admin', 'email-templates'],
                    },
                    {
                        label: 'Sms Templates',
                        icon: 'fa fa-bell',
                        routerLink: ['/admin', 'sms-templates'],
                    },
                    {
                        label: 'Event Workflow',
                        icon: 'fa fa-tasks',
                        routerLink: ['/admin', 'system-events-workflow'],
                    },
                    {
                        label: 'Email Logs',
                        icon: 'fa fa-list-alt',
                        routerLink: ['/admin', 'email-track-log'],
                    },
                ],
            },
            {
                label: 'Setups',
                icon: 'fa fa-gear',
                visible: authConfig.isSuperAdmin || authConfig.isAdministrator || (authConfig.isArtist && !authConfig.isEventArtist), // Hide for event-only artists
                items: [
                    {
                        label: 'Art Types',
                        icon: 'fa fa-paint-brush',
                        routerLink: ['/admin', 'art-type'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },
                    {
                        label: 'Sales Categories',
                        icon: 'fa fa-tags',
                        routerLink: ['/artists', 'art-wotk-categories'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator || (authConfig.isArtist && !authConfig.isEventArtist),
                    },
                    {
                        label: 'Product Series',
                        icon: 'fa fa-product-hunt',
                        routerLink: ['/artists', 'product-series'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator || (authConfig.isArtist && !authConfig.isEventArtist),
                    },
                    {
                        label: 'Product Materials',
                        icon: 'fa fa-shopping-basket',
                        routerLink: ['/artists', 'product-material'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator || (authConfig.isArtist && !authConfig.isEventArtist),
                    },
                    {
                        label: 'Product Features',
                        icon: 'fa fa-glass',
                        routerLink: ['/artists', 'product-glazes'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator || (authConfig.isArtist && !authConfig.isEventArtist),
                    },
                ],
            },
            {
                label: 'QUICK ACTIONS',
                icon: 'fa fa-gear',
                visible: authConfig.isSuperAdmin || authConfig.isAdministrator || (authConfig.isArtist && !authConfig.isEventArtist) || this.isGallery || authConfig.isEventArtist, // Show for event-only artists (only My Profile visible inside)
                items: [
                    {
                        label: 'New ArtWork',
                        icon: 'fa fa-product-hunt',
                        routerLink: ['/artists', 'artworks', 'create'],
                        visible: authConfig.isArtist && !authConfig.isEventArtist, // Hide for event-only artists
                    },
                    {
                        label: 'New Consignment',
                        icon: 'fa fa-truck',
                        routerLink: ['/view-consignments'],
                        visible: authConfig.isArtist && !authConfig.isEventArtist, // Hide for event-only artists
                    },
                    {
                        label: 'New Consignment',
                        icon: 'fa fa-truck',
                        routerLink: ['/view-consignments'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator,
                    },
                    {
                        label: 'Quick Tour',
                        icon: 'fa fa-map-signs',
                        visible: this.showQuickTour && !authConfig.isEventArtist, // Hide for event-only artists
                        command: () => this.startTour(),
                    },
                    {
                        label: 'Onboarding',
                        icon: 'fa fa-check-circle-o',
                        routerLink: ['artist-onboarding-checklist'],
                        visible: authConfig.isArtist && !authConfig.isEventArtist, // Hide for event-only artists
                    },
                    {
                        label: 'My Profile',
                        icon: 'fa fa-user',
                        routerLink: ['/edit-profile'],
                        visible: authConfig.isSuperAdmin || authConfig.isAdministrator || authConfig.isArtist || this.isGallery, // Show for ALL artists (including event-only)
                    },
                    {
                        label: 'My Website',
                        icon: 'fa fa-globe',
                        routerLink: ['/profile', this.authService.getUserName()],
                        visible: authConfig.isArtist && !authConfig.isEventArtist, // Hide for event-only artists
                    },
                ],
            },
        ];
    }

    ngAfterViewInit(): void { }

    public startTour(): void {
        if (this.isArtist) {
            this.currentStepIndex = 0;
            this.guidedTourService.startTour(this.artistTour);
            this.showDashboard = true;
            this.onBoardingService.updateLocalStorageState(true);
            this.executeNextStepLogic();
        } else if (this.isGallery) {
            this.currentStepIndex = 0;
            this.guidedTourService.startTour(this.galleryTour);
            this.showDashboard = true;
            this.onBoardingService.updateLocalStorageState(true);
            this.executeNextStepLogic();
        }
        setTimeout(() => {
            const skipBtnElements = document.getElementsByClassName('skip-button');
            console.log(`Found ${skipBtnElements.length} skip buttons using their classes.`);
            if (skipBtnElements.length > 0) {
                const skipBtn = skipBtnElements[0] as HTMLElement;
                skipBtn.innerText = 'Exit Tour';
                skipBtn.style.backgroundColor = 'rgba(254,160,6,1)';
                skipBtn.style.color = '#ffffff';
                skipBtn.style.zIndex = '1000';
            }
        }, 0);
    }

    public executeNextStepLogic() {
        this.showMore = false;
        setTimeout(() => {
            const toggleButtons = document.getElementsByClassName('dsButton');
            if (toggleButtons.length > 0) {
                Array.from(toggleButtons).forEach((button) => {
                    this.renderer.listen(button, 'click', () => this.toggleMore());
                });
            } else {
                console.error('No elements with class "dsButton" found.');
            }
        }, 100);
    }

    public goToNextStep(): void {
        this.executeNextStepLogic();

    }
}